import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { CheckCircle2, Loader2, AlertCircle, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * BillingSuccess. landed here from Stripe with ?session_id=...
 * Polls /api/billing/status/:session_id until payment_status='paid' or 'expired'.
 */
export default function BillingSuccess() {
 const [params] = useSearchParams();
 const sessionId = params.get("session_id");
 const [state, setState] = useState("polling"); // polling | paid | failed | timeout
 const [data, setData] = useState(null);
 const attemptsRef = useRef(0);
 const { refreshUser } = useAuth();

 useEffect(() => {
 if (!sessionId) { setState("failed"); return; }
 let cancelled = false;
 let timer = null;

 const poll = async () => {
 try {
 const { data } = await api.get(`/billing/status/${sessionId}`);
 if (cancelled) return;
 setData(data);
 if (data.payment_status === "paid") {
 setState("paid");
 try { await refreshUser?.(); } catch (_) {}
 return;
 }
 if (data.status === "expired") {
 setState("failed");
 return;
 }
 attemptsRef.current += 1;
 if (attemptsRef.current >= 30) { // ~60s of polling. long enough for slow card auth + webhook propagation
 setState("timeout");
 return;
 }
 timer = setTimeout(poll, 2000);
 } catch (e) {
 if (cancelled) return;
 attemptsRef.current += 1;
 if (attemptsRef.current >= 30) { setState("timeout"); return; }
 timer = setTimeout(poll, 2000);
 }
 };
 poll();
 return () => { cancelled = true; if (timer) clearTimeout(timer); };
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [sessionId]);

 return (
 <div className="min-h-screen paper-grain">
 <Navbar />
 <div className="max-w-xl mx-auto px-6 sm:px-8 py-20 text-center" data-testid="billing-success-page">
 {state === "polling" && (
 <>
 <Loader2 size={36} strokeWidth={1.5} className="mx-auto text-[#1B6C62] animate-spin mb-4" />
 <h1 className="font-serif text-3xl text-[#0D2B52] mb-2">Confirming your payment…</h1>
 <p className="text-[#6B7280] font-serif">This usually takes 5–10 seconds. Don't close this page.</p>
 </>
 )}

 {state === "paid" && (
 <>
 <CheckCircle2 size={44} strokeWidth={1.5} className="mx-auto text-[#1B6C62] mb-4" />
 <h1 className="font-serif text-3xl text-[#0D2B52] mb-2">Payment received. Welcome aboard.</h1>
 <p className="text-[#6B7280] font-serif mb-6">
 Your plan is active. Everything you need is unlocked.
 </p>
 <Link to="/dashboard" className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="billing-success-cta">
 Go to my dashboard <ArrowRight size={14} strokeWidth={1.5} />
 </Link>
 </>
 )}

 {state === "timeout" && (
 <>
 <AlertCircle size={36} strokeWidth={1.5} className="mx-auto text-[#8A6D3B] mb-4" />
 <h1 className="font-serif text-3xl text-[#0D2B52] mb-2">Still processing…</h1>
 <p className="text-[#6B7280] font-serif mb-6">
 Stripe is taking longer than usual to confirm. We'll auto-apply your plan as soon as Stripe finishes.
 You can safely close this page.
 </p>
 <Link to="/account" className="btn-secondary text-sm">Check my account</Link>
 </>
 )}

 {state === "failed" && (
 <>
 <AlertCircle size={36} strokeWidth={1.5} className="mx-auto text-[#B35D4C] mb-4" />
 <h1 className="font-serif text-3xl text-[#0D2B52] mb-2">Payment didn't go through</h1>
 <p className="text-[#6B7280] font-serif mb-6">
 No charge was made. You can try again from the pricing page.
 </p>
 <Link to="/pricing" className="btn-primary text-sm">Back to pricing</Link>
 </>
 )}

 {data?.amount_total != null && state === "paid" && (
 <p className="text-xs text-[#6B7280] mt-6 font-mono">
 Receipt: {data.currency?.toUpperCase()} {(data.amount_total / 100).toFixed(2)}
 </p>
 )}
 </div>
 </div>
 );
}
